import { createContext, PropsWithChildren, useState } from 'react'

type Tab = number

type TabContextType = {
  activeTab: number
  hasRenderedTab: (tab: Tab) => boolean
  setActiveTab: (tab: Tab) => void
}

export const TabsContext = createContext<TabContextType>({
  activeTab: 0,
  hasRenderedTab: () => {
    throw new Error('hasRendered must be used within TabContextType')
  },
  setActiveTab: () => {
    throw new Error('setActiveTab must be used within TabContextType')
  }
})

export type TabProviderProps = PropsWithChildren<{
  defaultTab?: number
}>

/**
 * Use to provide the active TabContext to consumers. DefaultTab can be
 * supplied in order to set the tab initially rendered.
 */
const TabsProvider = ({ children, defaultTab = 0 }: TabProviderProps) => {
  const [activeTab, setActiveTabState] = useState(defaultTab)
  const [renderedTabs, setRenderedTabs] = useState([defaultTab])

  const setActiveTab = (tab: Tab) => {
    const newRenderedTabs = new Set([...renderedTabs, tab])
    setRenderedTabs([...newRenderedTabs])
    setActiveTabState(tab)
  }

  const hasRenderedTab = (tab: Tab) => {
    return renderedTabs.indexOf(tab) > -1
  }

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab, hasRenderedTab }}>
      {children}
    </TabsContext.Provider>
  )
}

export default TabsProvider
